import { Observable, from, Subject } from 'rxjs';
import { inject, Injectable, signal, computed } from '@angular/core';
import { APP_CONFIG } from '../../../env/env.token';
import {
    HubConnection, HubConnectionBuilder, IHttpConnectionOptions, HttpTransportType, LogLevel
} from '@microsoft/signalr';
import { TradingProcedureStatusChangedMessage } from './types';
import { tap } from 'rxjs/operators';
import { SessionData } from '../../../app/auth/models/session-data.model';
import { LocalStorageService } from '../../services/browser-storage/local-storage.service';
import { LocalStorageKeys } from '../../services/browser-storage/types/local-storage.enum';

export type ConnState = 'idle' | 'connecting' | 'connected' | 'reconnecting' | 'stopped';

@Injectable({ providedIn: 'root' })
export class TradingProcedureReportingHubService {

    get tradingProcedureStatusChanged$(): Observable<TradingProcedureStatusChangedMessage> {
        return this._tpStatusChanged$.asObservable();
    }
    private readonly _tpStatusChanged$ = new Subject<TradingProcedureStatusChangedMessage>();

    private readonly appConfig = inject(APP_CONFIG);
    private readonly localStorage = inject(LocalStorageService);

    private connection: HubConnection;

    private readonly connSig = signal<ConnState>('idle');
    readonly isConnected = computed(() => this.connSig() === 'connected');

    constructor() {
        this.init();
    }

    private init(): void {
        this.connSig.set('connecting');

        const options: IHttpConnectionOptions = {
            transport: HttpTransportType.WebSockets,
            skipNegotiation: true,
            accessTokenFactory: () => this.localStorage.getItem<SessionData>(LocalStorageKeys.SessionData)?.accessToken,
        };

        this.connection = new HubConnectionBuilder()
            .withUrl(this.appConfig.tradingProcedureStatusReportingHubUrl, options)
            .withAutomaticReconnect()
            .configureLogging(LogLevel.Information)
            .build();

        this.connection.onreconnecting(() => this.connSig.set('reconnecting'));
        this.connection.onreconnected(() => this.connSig.set('connected'));
        this.connection.onclose(() => this.connSig.set('stopped'));

        this.connection.on('ReportTradingProcedureStatusChanged', args => {
            this._tpStatusChanged$.next(args);
        });

        from(this.connection.start())
            .pipe(
                tap(() => {
                    this.connSig.set('connected');
                })
            )
            .subscribe({
                error: err => {
                    this.connSig.set('stopped');
                    console.error('[Hub] connection error', err);
                }
            });
    }

    stop(): Observable<void> {
        return from(this.connection.stop());
    }
}


